// ABOUTME: Desktop icon sidebar for navigation
// ABOUTME: Minimal vertical icon bar with Home, Discover, Bookmarks, Notifications, Profile

"use client"

import { useState, useRef, useEffect, useMemo } from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faHouse,
  faCompass,
  faBookmark,
  faBell,
  faUser,
  faPlus,
  faPenToSquare,
  faLink,
  faGear,
} from "@fortawesome/free-solid-svg-icons"
import { UserButton } from "@clerk/nextjs"
import { DraftyIcon } from "../drafty-icon"
import { DraftyLogo } from "../drafty-logo"
import { useAddLinkModal } from "@/contexts/add-link-modal-context"

interface IconSidebarProps {
  username?: string
  unreadNotificationsCount?: number
}

export function IconSidebar({ username, unreadNotificationsCount = 0 }: IconSidebarProps) {
  const pathname = usePathname()
  const [createMenuOpen, setCreateMenuOpen] = useState(false)
  const { openAddLinkModal } = useAddLinkModal()
  const menuRef = useRef<HTMLDivElement>(null)

  // Memoize navItems to prevent recreation on every render
  const navItems = useMemo(() => [
    { href: "/home", icon: faHouse, label: "Home", isActive: pathname === "/home" },
    { href: "/discover", icon: faCompass, label: "Discover", isActive: pathname === "/discover" },
    { href: "/notifications", icon: faBell, label: "Notifications", isActive: pathname === "/notifications", badge: unreadNotificationsCount },
    { href: username ? `/@${username}` : "/home", icon: faUser, label: "My Blog", isActive: pathname === `/@${username}` },
    { href: "/bookmarks", icon: faBookmark, label: "Bookmarks", isActive: pathname === "/bookmarks" },
    { href: "/settings", icon: faGear, label: "Settings", isActive: pathname === "/settings" },
  ], [pathname, username, unreadNotificationsCount])

  // Memoize UserButton appearance to prevent object recreation
  const userButtonAppearance = useMemo(() => ({
    elements: {
      avatarBox: "w-9 h-9",
      userButtonTrigger: "rounded-full"
    }
  }), [])

  // Close menu on click outside
  useEffect(() => {
    if (!createMenuOpen) return

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setCreateMenuOpen(false)
      }
    }
    // Attach listener immediately, no setTimeout needed
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [createMenuOpen])

  // Close on escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setCreateMenuOpen(false)
    }
    if (createMenuOpen) {
      window.addEventListener("keydown", handleEscape)
      return () => window.removeEventListener("keydown", handleEscape)
    }
  }, [createMenuOpen])

  const handleOpenAddLink = () => {
    setCreateMenuOpen(false)
    openAddLinkModal()
  }

  return (
    <>
      <aside className="hidden md:flex fixed top-0 left-0 h-screen w-14 xl:w-52 bg-white dark:bg-neutral-900 border-r border-gray-200 dark:border-neutral-800 flex-col items-center xl:items-stretch py-3 xl:px-3 z-40">
        {/* Logo at top - icon on md, full logo centered on xl */}
        <Link
          href="/home"
          className="w-10 h-10 xl:w-auto xl:h-auto xl:py-2 xl:px-2 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-800 transition-colors"
          title="Drafty Home"
        >
          <DraftyIcon size={24} className="xl:hidden" />
          <DraftyLogo width={120} height={24} className="hidden xl:block" />
        </Link>

        {/* Spacer between logo and nav */}
        <div className="h-6" />

        {/* Navigation Items */}
        <nav className="flex flex-col items-center xl:items-stretch gap-1">
          {navItems.map((item) => (
            <Link
              key={item.label}
              href={item.href}
              className={`
                relative w-10 h-10 xl:w-auto xl:h-auto xl:py-2.5 xl:px-3 flex items-center justify-center xl:justify-start gap-3 rounded-lg
                transition-colors duration-200
                ${item.isActive
                  ? "bg-gray-100 dark:bg-neutral-800 text-gray-900 dark:text-gray-100"
                  : "text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-neutral-800 hover:text-gray-900 dark:hover:text-gray-100"
                }
              `}
              title={item.label}
            >
              <FontAwesomeIcon icon={item.icon} className="w-5 h-5 shrink-0" />
              <span className="hidden xl:inline text-sm font-medium">{item.label}</span>
              {item.badge !== undefined && item.badge > 0 && (
                <span className="absolute -top-1 -right-1 xl:static xl:ml-auto min-w-[18px] h-[18px] flex items-center justify-center px-1 text-xs font-medium text-white bg-pink-600 rounded-full">
                  {item.badge > 99 ? "99+" : item.badge}
                </span>
              )}
            </Link>
          ))}
        </nav>

        {/* Create Button with dropdown - below nav */}
        <div className="relative mt-4 xl:w-full" ref={menuRef}>
          <button
            onClick={() => setCreateMenuOpen(!createMenuOpen)}
            className="w-10 h-10 xl:w-full xl:h-auto xl:py-2.5 xl:px-3 flex items-center justify-center xl:justify-start gap-3 rounded-lg bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors"
            title="Create"
          >
            <FontAwesomeIcon icon={faPlus} className="w-4 h-4 shrink-0" />
            <span className="hidden xl:inline text-sm font-medium">Create</span>
          </button>

          {/* Create Dropdown */}
          {createMenuOpen && (
            <div className="absolute left-full xl:left-0 top-0 xl:top-full ml-2 xl:ml-0 xl:mt-2 w-48 xl:w-full bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg shadow-lg overflow-hidden z-50">
              <Link
                href="/new"
                onClick={() => setCreateMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-neutral-700 transition-colors"
              >
                <FontAwesomeIcon icon={faPenToSquare} className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                <span className="text-sm font-medium">Post</span>
              </Link>
              <button
                onClick={handleOpenAddLink}
                className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-neutral-700 transition-colors border-t border-gray-100 dark:border-neutral-700"
              >
                <FontAwesomeIcon icon={faLink} className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                <span className="text-sm font-medium">Link</span>
              </button>
            </div>
          )}
        </div>

        {/* Spacer to push account to bottom */}
        <div className="flex-1" />

        {/* Account - Clerk UserButton at bottom */}
        <div
          className="mb-1 xl:w-full xl:py-2 xl:px-2 flex items-center justify-center xl:justify-start gap-3 rounded-lg xl:hover:bg-gray-100 xl:dark:hover:bg-neutral-800 transition-colors cursor-pointer"
          onClick={(e) => {
            // If clicking the label area, trigger click on the UserButton
            const target = e.target as HTMLElement
            if (!target.closest('button')) {
              const btn = e.currentTarget.querySelector('button')
              btn?.click()
            }
          }}
        >
          <UserButton
            afterSignOutUrl="/"
            appearance={userButtonAppearance}
          />
          <span className="hidden xl:inline text-sm font-medium text-gray-500 dark:text-gray-400">Account</span>
        </div>
      </aside>
    </>
  )
}
